import React from 'react';

import { Badge } from '@ast/magma/components/badge/badge';

import { AppStoreIconLink, AppStoreType } from '@app/common/components/controls/AppStoreIconLink/AppStoreIconLink';
import { FlexContainer } from '@app/common/components/FlexContainer/FlexContainer';

import { useUserLoginTranslation } from '@app/widgets/user-login/hooks/useUserLoginTranslation';

import { QuickLoginSetupMode } from '../../../constants';

import styles from './QuickLoginSetup.pcss';

export interface QuickLoginSetupProp {
  readonly setupMode: QuickLoginSetupMode;
}

export const QuickLoginSetup: React.FC<QuickLoginSetupProp> = ({
  setupMode,
}) => {
  const { t } = useUserLoginTranslation();

  // eslint-disable-next-line max-len
  const altBefore = t('login.steps.quick-login.setup.installation-required.list.install-app.title|Quick Login Setup install app title',
    'Install the free mobile app on your device.');

  const installationRequiredData = [
    {
      countText: t('login.steps.quick-login.setup.installation-required.list.install-app.listnumber|Quick Login install app list number',
        'One.'),
      itemText:
  <>
    {altBefore}
    <FlexContainer direction="row" gap="small" wrap={false}>
      <AppStoreIconLink appStoreType={AppStoreType.AppleAppStore} altBefore={altBefore} />
      <AppStoreIconLink appStoreType={AppStoreType.AndroidPlayMarket} altBefore={altBefore} />
    </FlexContainer>
  </>,
    },
    {
      countText: t('login.steps.quick-login.setup.installation-required.list.signin.listnumber|Quick Login sign in list number',
        'Two.'),
      itemText: t('login.steps.quick-login.setup.installation-required.list.signin.title|Quick Login Setup sign in title',
        'Once installed, open the app and sign in using your online banking credentials.'),
    },
    {
      countText: t('login.steps.quick-login.setup.installation-required.list.enable-biometric.listnumber|Quick Login enable biometric list number',
        'Three.'),
      itemText: t('login.steps.quick-login.setup.installation-required.list.enable-biometric.title|Quick Login Setup enable biometric title',
        'Within the app, navigate to the Settings area and follow the instructions to enable biometric authentication for this device.'),
    },
    {
      countText: t('login.steps.quick-login.setup.installation-required.list.allow-pn.listnumber|Quick Login allow push notifications list number',
        'Four.'),
      itemText: t('login.steps.quick-login.setup.installation-required.list.allow-pn.title|Quick Login Setup allow push notifications title',
        'Allow the app to send push notifications to your device so we can send you login requests.'),
    },
  ];

  const biometricSetupRequiredData = [
    {
      countText: t('login.steps.quick-login.setup.biometric-required.list.enable-biometric.listnumber|Quick Login Setup enable biometric list number',
        'One.'),
      itemText: t('login.steps.quick-login.setup.biometric-required.list.enable-biometric.title|Quick Login Setup enable biometric title',
        'Enable biometric authentication. Within the app, navigate to Settings and follow the instructions on your device.'),
    },
    {
      countText: t('login.steps.quick-login.setup.biometric-required.list.enable-pushnotifications.listnumber|Quick Login Setup enable push notifications list number',
        'Two.'),
      itemText: t('login.steps.quick-login.setup.biometric-required.list.enable-pushnotifications.title|Quick Login Setup enable push notifications title',
        'Allow the app to send push notifications to your device so we can send you login requests.'),
    },
  ];

  const description = setupMode === QuickLoginSetupMode.InstallationRequired
    ? t('login.steps.quick-login.setup.installation-required.list.title|Quick Login Setup installation required list title',
      'Before using biometric login, please make sure you\'ve done the following:')
    : t('login.steps.quick-login.setup.biometric-required.list.title|Quick Login Setup installation required list title',
      'Please log into your mobile banking app and make sure the following has been done:');
  // eslint-disable-next-line max-len
  const instructions = setupMode === QuickLoginSetupMode.InstallationRequired ? installationRequiredData : biometricSetupRequiredData;

  return (
    <div>
      <p>{description}</p>
      <FlexContainer direction="column" gap="small" className={styles.flexColumn}>
        {instructions.map((value, index) => (
          <FlexContainer
            direction="row"
            gap="small"
            wrap={false}
            key={value.countText}
          >
            <Badge
              aria-label={value.countText}
              color="primary"
              display="filled"
              count={index + 1}
            />
            <span>{value.itemText}</span>
          </FlexContainer>
        ))}
      </FlexContainer>
    </div>
  );
};
